import React from "react";
import { ChamberMemberProGovernanceSettingsReturn } from "../../../graphql/types";
import { FormMultiSelectField } from "../../common/dashboard/FormFields";
import { SectionShell } from "../../common/dashboard/SectionShell";
import { useCmpOrganizationNoun } from "../../../contexts/CmpOrganizationNounContext";

export type GovernanceInvolvementCardProps = {
  id: string;
  governance?: ChamberMemberProGovernanceSettingsReturn | null;
  selected: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  loading?: boolean;
  error?: string;
  footer?: React.ReactNode;
};

const groupTypes = [
  { key: "boards", label: "Board of Directors" },
  { key: "committees", label: "Committees" },
  { key: "taskForces", label: "Task Forces" },
] as const;

const GovernanceInvolvementCard = ({
  id,
  governance,
  selected,
  onChange,
  disabled = false,
  loading = false,
  error,
  footer,
}: GovernanceInvolvementCardProps) => {
  const noun = useCmpOrganizationNoun();
  const validIds = new Set(groupTypes.flatMap(({ key }) => (governance?.[key] || []).map((group) => group.id)));
  const currentSelection = Array.from(new Set(selected.filter((value) => validIds.has(value))));

  return (
    <SectionShell
      id={id}
      variant="design"
      title="Member Governance Committees & Task Forces Involvement"
      description={`Open each dropdown below and check off every group this member belongs to. Your selections are listed under the dropdown and can be removed there. A member can belong to more than one group. Boards, committees, and task forces are created under ${noun.Singular} Set Up → Create Governance & Groups, and any name added, renamed, or deleted there updates these dropdowns automatically.`}
      footer={footer}
    >
      {error ? (
        <p role="alert" className="text-sm text-red-600">
          Governance groups could not be loaded. Refresh the page to try again.
        </p>
      ) : !governance ? (
        <p role="status" className="text-sm text-[#727272]">Loading governance groups...</p>
      ) : <div className="grid gap-6" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(240px, 100%), 1fr))" }}>
        {groupTypes.map(({ key, label }) => {
          const options = governance[key];
          const optionIds = options.map((group) => group.id);
          return (
            <FormMultiSelectField
              key={key}
              id={`${id}-${key}`}
              label={label}
              options={optionIds}
              getOptionLabel={(value) => options.find((group) => group.id === value)?.name || value}
              selected={currentSelection.filter((value) => optionIds.includes(value))}
              onChange={(values) => onChange([
                ...currentSelection.filter((value) => !optionIds.includes(value)),
                ...values.filter((value) => optionIds.includes(value)),
              ])}
              placeholder={`Select ${label}`}
              disabled={disabled || loading || options.length === 0}
              showSelectionsBelow
              wrapLabels
              emptyOptionsText="None created yet."
              emptySelectionText="No groups selected."
              menuMaxHeight={220}
            />
          );
        })}
      </div>}
    </SectionShell>
  );
};

export default GovernanceInvolvementCard;
